import {AfterViewInit, Component, Input, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {
  ApDynComponentComponent
}                                                                      from '../../ap-dyncomponent/ap-dyncomponent.component';
import {BehaviorSubject, combineLatest, Observable, of, Subscription} from 'rxjs';
import {
  ApDynGridColumnConfigBase
}                                                                     from '../../ap-dyngrids/config/ap-dyn-grid-column-config-base';
import {
  ApDynGridPagerWizardButton,
  ApDynGridPagerWizardConfig,
  ApDynGridPagerWizardSection
}                                                                     from '../../ap-dyngrids/config/ap-dyn-grid-pager-wizard-config';
import {
  FieldStore
}                                                                     from '../../stores/farm/field.store';
import {filter, map, skip}                                            from 'rxjs/operators';
import {ApTimeSpanUtil}                                               from '../../ap-utils';
import {ObjectFactory}                                                from 'ts-tooling';
import {
  SettingsStore
}                                                                     from '../../stores/base-data/settings.store';
import {
  IPpEntryPlanBookingEditData,
  IPpEntryPlanBookingGridItem,
  IPpPlanBookingComponentData
}                                                                     from './plan-booking/pp-plan-booking.types';
import {
  LanguageStore
}                                                                     from '../../stores/translation/language.store';
import {
  ApUtilService
}                                                                     from '../../ap-utils/service/ap-util.service';
import {
  FormStore, IEntryComponent
} from '../../stores/layout/form.store';
import {
  MachineStore
}                                                                     from '../../stores/docu/machine.store';
import {
  InstrumentStore
}                                                                     from '../../stores/docu/instrument.store';
import {
  DriverStore
}                                                                     from '../../stores/docu/driver.store';
import {
  LoginStore
}                                                                     from '../../stores/login/login.store';
import {
  IModalDialogData,
  ModalDialogButtonDisable
}                                                                     from '../../ap-interface/interfaces/ap-modaldialog-data.interface';
import {
  ModalDialogStore
}                                                                     from '../../stores/dialog/modal.dialog.store';
import {
  ApTranslationService
}                                                                     from '../../ap-utils/service/ap-translation.service';
import {
  ApDateService
}                                                                     from '../../ap-core/services/ap-date-service';
import {
  PlantProtectionPlanBookStore
}                                                                     from '../../stores/plant-protection/plant-protection-planbook.store';
import {
  PpPlanBookingGridConfig
}                                                                     from './plan-booking/pp-plan-booking-grid.config';
import {
  PpPlanBookingFieldsConfig
}                                                                     from './plan-booking/pp-plan-booking-fields.config';
import IGuid = System.IGuid;

@Component({
    selector: 'pp-plan-booking',
    template: `
    <ap-dyncomponent [headerIcon]="'ap-fungidetect-icon'"
                     [loading$]="loading$"
                     [columns]="columns$ | async"
                     [items]="items$ | async"
                     [pager]="pager"
                     (edit)="onEdit($event)">
    </ap-dyncomponent>
  `,
  standalone: false,
  providers: [
    PpPlanBookingGridConfig,
    PpPlanBookingFieldsConfig
  ]
})
export class PpPlanBookingComponent implements OnInit, AfterViewInit, OnDestroy, IEntryComponent {
  /**
   * Implements IEntryComponent
   */
  public Caption$: BehaviorSubject<string> = new BehaviorSubject('');
  @ViewChild(ApDynComponentComponent, {static: true}) public dynComponent: ApDynComponentComponent;
  public items$ = new BehaviorSubject<IPpEntryPlanBookingGridItem[]>([]);
  public columns$: Observable<ApDynGridColumnConfigBase[]> = of([]);
  public loading$: Observable<boolean> = of(false);
  public pager: ApDynGridPagerWizardConfig;
  public caption: string;

  @Input() public data: IPpPlanBookingComponentData;

  private _subscriptions: Subscription[] = [];

  constructor(private formStore: FormStore,
              private fieldStore: FieldStore,
              private loginStore: LoginStore,
              private driverStore: DriverStore,
              private machineStore: MachineStore,
              private settingsStore: SettingsStore,
              private languageStore: LanguageStore,
              private instrumentStore: InstrumentStore,
              private modalDialogStore: ModalDialogStore,
              private plantProtectionPlanBookStore: PlantProtectionPlanBookStore,
              private dateService: ApDateService,
              private translationService: ApTranslationService,
              private planBookingGridConfig: PpPlanBookingGridConfig,
              private planBookingFieldsConfig: PpPlanBookingFieldsConfig) {
  }

  public ngOnInit(): void {
    this.Caption$.next(this.data.IsBooked ? 'Global__Reset' : 'Global__Bookings');
    this.setLoading();
    this.setColumns();
    this.setPager();
  }

  public ngAfterViewInit(): void {
    this.setItems();
    this._subscriptions.push(
      this.dynComponent.SelectedItems$.subscribe((items: IPpEntryPlanBookingGridItem[]) => {
        const fieldsToSelect: string[] = items.filter(x => x.FieldId)
          .map(i => i.FieldId.toString());
        this.fieldStore.changeSelectedField(fieldsToSelect);
      })
    );
    this._subscriptions.push(
      this.items$.subscribe((items: IPpEntryPlanBookingGridItem[]): void => {
        const itemsToSelect = items.map(i => i.Id);
        setTimeout(() => this.dynComponent.dynGrid.gridPaging.setSelectedKeys(itemsToSelect));
      })
    );
  }

  public ngOnDestroy(): void {
    this._subscriptions.forEach(s => s.unsubscribe());
  }

  public onEdit(dataItem: IPpEntryPlanBookingGridItem): void {
    const singleEdit = this.planBookingFieldsConfig.getEditObject(dataItem);
    const dialog = {
      title: this.translationService.translate('Global__Edit'),
      show: true,
      buttons: [
        {
          key: 'cancel',
          text: 'Global__Cancel',
          primary: false
        },
        {
          key: 'apply',
          text: 'Global__Ok',
          primary: true,
          disable: ModalDialogButtonDisable.FormInvalid,
        }
      ],
      formConfig: {
        fieldSets: this.planBookingFieldsConfig.getModalFieldSets(dataItem, singleEdit,
          this.modalDialogStore.listenChanges()),
        validators: undefined,
        asyncValidators: undefined,
        values: ObjectFactory.Copy(singleEdit)
      }
    } as IModalDialogData;
    const resultSubscription = this.modalDialogStore.Listen(s => s.result)
      .pipe(skip(1))
      .subscribe((result) => {
        if (result?.key === 'apply') {
          const formValues = ObjectFactory.Copy(result.formValues) as IPpEntryPlanBookingEditData;
          this.updateOverviewItemsFromSingleEdit(dataItem.Id, formValues);
          resultSubscription.unsubscribe();
        } else if (result?.key === 'cancel') {
          resultSubscription.unsubscribe();
        }
      });
    this.modalDialogStore.setModalDialogData(dialog);
  }

  private setLoading(): void {
    this.loading$ = combineLatest([
      this.fieldStore.Listen(x => x.loading),
      this.settingsStore.Listen(x => x.loading),
      this.languageStore.Listen(x => x.loading),
      this.driverStore.Listen(x => x.loading),
      this.instrumentStore.Listen(x => x.loading),
      this.machineStore.Listen(x => x.loading)
    ]).pipe(
      map((loadings) => loadings.some((x) => x))
    );
  }

  private setColumns(): void {
    this.columns$ = combineLatest([
      this.languageStore.SelectedLanguage$,
      this.settingsStore.FirstSetting$
    ]).pipe(
      filter(([language, settings]) => !!language && !!settings),
      map(([language, settings]) => {
        return this.planBookingGridConfig.getGridColumns(language, settings);
      })
    );
  }

  private setPager(): void {
    const buttonTitle = this.data.IsBooked ? 'Global__Reset' : 'Admin_Pages_Popups__Book';
    this.pager = new ApDynGridPagerWizardConfig(
      new ApDynGridPagerWizardButton(
        'Global__Cancel',
        ApUtilService.createEventEmitter(this.cancel.bind(this), this._subscriptions)
      ),
      new ApDynGridPagerWizardSection(),
      new ApDynGridPagerWizardButton(
        buttonTitle,
        ApUtilService.createEventEmitter(this.booking.bind(this), this._subscriptions),
        {
          primary: true,
          active: this.dynComponent.SelectedItems$.pipe(map((items) => items.length !== 0))
        }
      ),
      {
        formConfig: {
          showMassEdit: false,
          onSubmit: ApUtilService.createEventEmitter(this.updateOverviewItemsFromPager.bind(this), this._subscriptions),
          config: this.planBookingFieldsConfig.getPagerFieldSets()
        }
      }
    );
  }

  private setItems(): void {
    this._subscriptions.push(
      combineLatest([
        this.fieldStore.Fields$,
        this.driverStore.Drivers$,
        this.machineStore.Machines$,
        this.instrumentStore.Instruments$,
        this.settingsStore.FirstSetting$,
        this.languageStore.SelectedLanguage$,
        this.plantProtectionPlanBookStore.PlanBooks$
      ]).pipe(
        filter(([_, _1, _2, _3, _4, _5, planBooks]) => !!planBooks && !!this.data.DataItem),
        map(([_, _1, _2, _3, _4, _5, planBooks]) => {
          const planIds = this.data.DataItem.map(x => x.Id);
          const plansToShow = planBooks.filter(x => planIds.includes(x.Id));
          return this.planBookingGridConfig.getItems(plansToShow, this.data.IsBooked);
        })
      ).subscribe((items) => {
        setTimeout(() => this.items$.next(items), 10);
      })
    );
  }

  private updateOverviewItemsFromPager(submitData: { ids: any[], value: any }): void {
    const planBookIds = submitData.ids as IGuid[];
    const singleEdit = ObjectFactory.Copy(submitData.value) as IPpEntryPlanBookingEditData;
    const planBooks = this.items$.getValue().filter(x => !!x);
    if (planBooks?.length > 0) {
      const updatedItems = planBooks.map((planBook: IPpEntryPlanBookingGridItem) => {
        if (planBookIds.includes(planBook.Id)) {
          this.planBookingGridConfig.updateGridItems(planBook, singleEdit, true);
        }
        return planBook;
      });
      setTimeout(() => this.items$.next(updatedItems));
    }
  }

  private updateOverviewItemsFromSingleEdit(planBookId: IGuid, singleEdit: IPpEntryPlanBookingEditData): void {
    const planBooks = this.items$.getValue().filter(x => !!x);
    if (planBooks?.length > 0) {
      const updatedItems = planBooks.map((planBook: IPpEntryPlanBookingGridItem) => {
        if (planBookId === planBook.Id) {
          this.planBookingGridConfig.updateGridItems(planBook, singleEdit, false);
        }
        return planBook;
      });
      setTimeout(() => this.items$.next(updatedItems));
    }
  }

  private booking(): void {
    const selectedItems = this.dynComponent.getSelectedItems() as IPpEntryPlanBookingGridItem[];
    if (selectedItems?.length === 0) {
      return;
    }
    const planBooksForUpdate = selectedItems.map(selectedItem => {
      const copiedSourceItem = ObjectFactory.Copy(selectedItem.SourceItem);
      copiedSourceItem.BookedAt = !this.data.IsBooked ? this.dateService.getUtc() : null;
      copiedSourceItem.BookedBy = !this.data.IsBooked ? this.loginStore.UserId : null;
      copiedSourceItem.Start = ApTimeSpanUtil.getEmptyTimeSpan();
      copiedSourceItem.Duration = ApTimeSpanUtil.getTimeSpanFromMilliseconds(selectedItem.DurationInMilliseconds, true);
      copiedSourceItem.ApplicationDate = this.dateService.getDateNoon(selectedItem.ApplicationDate);
      copiedSourceItem.ExportDriver = selectedItem.DriverId;
      copiedSourceItem.ExportMachine = selectedItem.MachineId;
      copiedSourceItem.ExportInstrument = selectedItem.InstrumentId;
      return copiedSourceItem;
    }).filter(x => !!x);
    if (planBooksForUpdate?.length > 0) {
      const dataToSend = planBooksForUpdate.map(planBook => ({planBook, logFile: undefined}));
      if (this.data.IsBooked) {
        this.plantProtectionPlanBookStore.unBookPlanBooks(dataToSend);
      } else {
        this.plantProtectionPlanBookStore.bookPlanBooks(dataToSend);
      }
    }
    this._onClose();
  }

  private _onClose(): void {
    this.formStore.closeForm();
  }

  private cancel() {
    this._onClose();
    this.formStore.navigateToParamUrl();
  }
}
